/**
 * <ultra-carousel> — dependency-free scroll-snap carousel.
 *
 * Markup:
 *   <ultra-carousel data-autoplay="3000">
 *     <ul class="ultra-carousel__track"> <li class="ultra-carousel__slide">…</li> … </ul>
 *     <div class="ultra-carousel__nav">
 *       <button data-carousel-prev>…</button> <button data-carousel-next>…</button>
 *     </div>
 *   </ultra-carousel>
 *
 * Attributes:
 *   data-autoplay   milliseconds between slides (optional, pauses on hover/focus/touch)
 */
if (!customElements.get('ultra-carousel')) {
  class UltraCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ultra-carousel__track');
      if (!this.track) return;

      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.onScroll = this.updateButtons.bind(this);

      this.prev?.addEventListener('click', () => this.scrollByPage(-1));
      this.next?.addEventListener('click', () => this.scrollByPage(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      const delay = parseInt(this.dataset.autoplay, 10);
      if (delay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.startAutoplay(delay);
      }

      this.updateButtons();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.stopAutoplay();
    }

    get step() {
      const slide = this.track.querySelector('.ultra-carousel__slide');
      if (!slide) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    scrollByPage(direction) {
      this.track.scrollBy({ left: direction * this.step, behavior: 'smooth' });
    }

    updateButtons() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth - 2;
      if (this.prev) this.prev.disabled = scrollLeft <= 2;
      if (this.next) this.next.disabled = scrollLeft >= max;

      const nav = this.querySelector('.ultra-carousel__nav');
      if (nav) nav.hidden = scrollWidth <= clientWidth + 2;
    }

    startAutoplay(delay) {
      const tick = () => {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        if (scrollLeft >= scrollWidth - clientWidth - 2) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.scrollByPage(1);
        }
      };
      this.timer = window.setInterval(tick, delay);
      ['mouseenter', 'focusin', 'touchstart'].forEach((type) =>
        this.addEventListener(type, () => this.stopAutoplay(), { passive: true })
      );
    }

    stopAutoplay() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  customElements.define('ultra-carousel', UltraCarousel);
}
